"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const descriptor = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const tools = [{
    name: "Neural",
    kanji: "AI",
    subtitle: "CUSTOMER SUPPORT AUTOMATION",
    blurb: "We deploy intelligent conversational agents that handle inquiries with human-like empathy and machine-speed precision. By integrating advanced natural language processing, your business provides 24/7 support that scales effortlessly, ensuring no customer is ever left waiting.",
    meta: "ORIGIN: MODERN / ROLE: SUPPORT SCALING",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_support_agent.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_support_agent.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_support_agent.webp-wvc-sizes",
    alt: "AI customer support interface on a digital screen"
  }, {
    name: "Sync",
    kanji: "API",
    subtitle: "APPOINTMENT BOOKING SYSTEMS",
    blurb: "Eliminate the friction of manual scheduling. Our automated booking systems sync directly with your calendar, managing availability and reminders in real-time. We turn your administrative burden into a seamless, self-service experience for your clients.",
    meta: "ORIGIN: MODERN / ROLE: SCHEDULING EFFICIENCY",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automated_booking_system.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automated_booking_system.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automated_booking_system.webp-wvc-sizes",
    alt: "Digital calendar interface showing automated booking slots"
  }, {
    name: "Flow",
    kanji: "ERP",
    subtitle: "INVOICING AUTOMATION",
    blurb: "Precision in every transaction. Our automated invoicing workflows trigger payments, track status, and reconcile accounts without human intervention. Reduce errors and accelerate your cash flow with systems designed for absolute financial accuracy.",
    meta: "ORIGIN: MODERN / ROLE: FINANCIAL PRECISION",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/invoicing_automation_flow.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/invoicing_automation_flow.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/invoicing_automation_flow.webp-wvc-sizes",
    alt: "Automated financial dashboard showing invoice processing"
  }, {
    name: "Pulse",
    kanji: "CRM",
    subtitle: "EMAIL MARKETING AUTOMATION",
    blurb: "Deliver the right message at the perfect moment. Our marketing automation engines segment your audience and trigger personalized campaigns based on user behavior. We turn raw data into meaningful engagement that drives long-term customer loyalty.",
    meta: "ORIGIN: MODERN / ROLE: AUDIENCE ENGAGEMENT",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/email_marketing_automation.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/email_marketing_automation.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/email_marketing_automation.webp-wvc-sizes",
    alt: "Email marketing analytics dashboard with automated triggers"
  }, {
    name: "Logic",
    kanji: "BOT",
    subtitle: "AI CHATBOTS",
    blurb: "Beyond simple scripts, our AI chatbots understand context and intent. Whether qualifying leads or guiding users through complex processes, these bots act as your digital workforce, operating with consistent logic and tireless dedication to your brand goals.",
    meta: "ORIGIN: MODERN / ROLE: LEAD QUALIFICATION",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_chatbot_interface.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_chatbot_interface.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_chatbot_interface.webp-wvc-sizes",
    alt: "Advanced AI chatbot interface interacting with a user"
  }, {
    name: "Nexus",
    kanji: "INT",
    subtitle: "WORKFLOW INTEGRATION",
    blurb: "We connect your disparate software tools into a unified, high-performance ecosystem. By bridging the gaps between your platforms, we ensure data flows seamlessly, eliminating silos and empowering your team to focus on high-value strategic work.",
    meta: "ORIGIN: MODERN / ROLE: SYSTEM INTEGRATION",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/workflow_integration_nexus.webp",
    srcset: "/img/workflow_integration_nexus.webp-wvc-nexus.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/workflow_integration_nexus.webp-wvc-sizes",
    alt: "Network diagram showing integrated software workflows"
  }];
  return <section data-section-id="5202" id="the-tech-stack" className="bg-background text-foreground py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div ref={header.ref} className={`grid grid-cols-12 gap-6 mb-20 md:mb-28 lg:mb-32 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase" style={{
            color: "var(--chart-5)"
          }}>
              Our Tech Stack
            </span>
          </div>
          <div className="col-span-12 md:col-span-9 md:col-start-3 lg:col-span-8 lg:col-start-3">
            <h2 className="font-default font-light text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-foreground">
              The architecture
              <br />
              of{" "}
              <span className="italic" style={{
              color: "var(--foreground)"
            }}>
                autonomy.
              </span>
            </h2>
            <p ref={descriptor.ref} className={`mt-8 italic text-base md:text-lg max-w-xl transition-all duration-1000 delay-200 ease-out ${descriptor.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            color: "var(--muted-foreground)"
          }}>
              Precision tools for the modern digital enterprise.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-x-6 gap-y-20 md:gap-y-24">
          <article data-index={0} className="col-span-12 md:col-span-5 group">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={tools[0].src} data-wvc-srcset={tools[0].srcset} data-wvc-sizes={tools[0].sizes} alt={tools[0].alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
            </div>
            <ToolCaption tool={tools[0]} />
          </article>

          <article data-index={1} className="col-span-12 md:col-span-7 md:mt-20 group">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="aspect-[3/2] overflow-hidden">
                <img src={tools[1].src} data-wvc-srcset={tools[1].srcset} data-wvc-sizes={tools[1].sizes} alt={tools[1].alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
            </div>
            <ToolCaption tool={tools[1]} />
          </article>

          <article data-index={2} className="col-span-12 md:col-span-4 group">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="aspect-square overflow-hidden">
                <img src={tools[2].src} data-wvc-srcset={tools[2].srcset} data-wvc-sizes={tools[2].sizes} alt={tools[2].alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
            </div>
            <ToolCaption tool={tools[2]} />
          </article>

          <article data-index={3} className="col-span-12 md:col-span-3 md:col-start-5 md:mt-16 group">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={tools[3].src} data-wvc-srcset={tools[3].srcset} data-wvc-sizes={tools[3].sizes} alt={tools[3].alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
            </div>
            <ToolCaption tool={tools[3]} />
          </article>

          <article data-index={4} className="col-span-12 md:col-span-7 group">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="aspect-[3/1] overflow-hidden">
                <img src={tools[4].src} data-wvc-srcset={tools[4].srcset} data-wvc-sizes={tools[4].sizes} alt={tools[4].alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
            </div>
            <ToolCaption tool={tools[4]} />
          </article>

          <article data-index={5} className="col-span-12 md:col-span-5 md:col-start-8 group">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={tools[5].src} data-wvc-srcset={tools[5].srcset} data-wvc-sizes={tools[5].sizes} alt={tools[5].alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
            </div>
            <ToolCaption tool={tools[5]} />
          </article>
        </div>
      </div>
    </section>;
}
function ToolCaption({
  tool
}: {
  tool: {
    name: string;
    kanji: string;
    subtitle: string;
    blurb: string;
    meta: string;
  };
}) {
  return <div className="mt-6 relative">
      <span aria-hidden="true" className="absolute top-0 left-0 h-px w-0 group-hover:w-full transition-[width] duration-500 ease-out" style={{
      backgroundColor: "var(--foreground)"
    }} />
      <div className="pt-5">
        <h3 className="font-default italic text-[26px] md:text-[28px] leading-tight text-foreground">
          {tool.name}{" "}
          <span className="not-italic" style={{
          color: "var(--muted-foreground)"
        }}>
            {tool.kanji}
          </span>
        </h3>
        <p className="mt-2 font-mono text-[11px] tracking-[0.25em] uppercase" style={{
        color: "var(--muted-foreground)"
      }}>
          {tool.subtitle}
        </p>
        <p className="mt-4 font-default text-[15px] leading-[1.7] max-w-prose" style={{
        color: "var(--foreground)"
      }}>
          {tool.blurb}
        </p>
        <p className="mt-5 font-mono text-[11px] tracking-[0.2em] uppercase" style={{
        color: "var(--foreground)"
      }}>
          {tool.meta}
        </p>
      </div>
    </div>;
}